// The event's rules (Lane 1344, Phase 3 slice 3B): what every scenario may use.
// A rule made here is confirmed when saved; a rule from the chat is proposed until
// the buyer confirms it. Supplier notes never become rules. Rules never reach a
// supplier.
import React, { useCallback, useEffect, useState } from 'react';
import { RULE_TYPES, STATE_TEXT, SOURCE_TEXT, categoryLabeler, ruleText } from './scenarioText';

const NEEDS_SUPPLIER = ['keep', 'exclude', 'all_or_nothing', 'supplier_limits'];
const EVENT_WIDE_OK = ['max_suppliers', 'prefer_fewer_suppliers', 'prefer_fewer_moves', 'not_modeled',
  'supplier_limits', 'lead_time_limit', 'qualification_cost'];
// Rules that offer "the whole event" or "only these lines" (Lane 1395 adds three).
const SCOPE_CHOICE = ['max_suppliers', 'supplier_limits', 'lead_time_limit', 'qualification_cost'];
const amountOrNull = (v) => (String(v).trim() === '' ? null : String(v).trim());

function emptyForm() {
  return { rule_type: 'keep', supplier_id: '', supplier_b: '', share_a: 1, share_b: 1, n: 2,
    percent: '', share_pct: 20, amount: '', description: '', allow_no_change: false,
    per: 'part', years: 1, days: 42, max_amount: '', min_amount: '', no_new_parts: false, risk_percent: '',
    scope: 'filter', level_1: '', level_2: '', site: '', search: '' };
}

function formToInput(f) {
  const input = { rule_type: f.rule_type };
  if (NEEDS_SUPPLIER.includes(f.rule_type)) input.supplier_id = Number(f.supplier_id) || null;
  if (f.rule_type === 'split') input.shares = { [f.supplier_id]: Number(f.share_a), [f.supplier_b]: Number(f.share_b) };
  if (['max_suppliers', 'min_sources'].includes(f.rule_type)) input.n = Number(f.n);
  if (f.rule_type === 'min_sources') { input.share_n = Number(f.share_pct); input.share_d = 100; }
  if (f.rule_type === 'threshold') {
    // A percent, dollars a year, or both (Lane 1395).
    if (String(f.percent).trim() !== '') input.percent = Number(f.percent);
    if (amountOrNull(f.amount) !== null) { input.amount = amountOrNull(f.amount); input.per = f.per === 'site' ? 'site' : 'part'; }
  }
  if (f.rule_type === 'supplier_limits') {
    input.max_amount = amountOrNull(f.max_amount);
    input.min_amount = amountOrNull(f.min_amount);
    input.no_new_parts = Boolean(f.no_new_parts);
    if (String(f.risk_percent).trim() !== '') input.risk_percent = Number(f.risk_percent);
  }
  if (f.rule_type === 'lead_time_limit') input.days = Number(f.days);
  if (f.rule_type === 'qualification_cost') {
    input.amount = amountOrNull(f.amount);
    input.per = f.per === 'supplier' ? 'supplier' : 'part';
    input.years = Number(f.years) || 1;
  }
  if (f.rule_type === 'one_supplier') input.allow_no_change = Boolean(f.allow_no_change);
  if (['prefer_fewer_suppliers', 'prefer_fewer_moves'].includes(f.rule_type)) input.amount = f.amount;
  if (f.rule_type === 'not_modeled') input.description = f.description.trim();
  const eventWide = EVENT_WIDE_OK.includes(f.rule_type) && f.scope === 'all';
  if (f.chosenLines && !eventWide) {
    input.selection = { line_uids: f.chosenLines };   // an edited rule keeps the lines it named
  } else if (!eventWide && !['prefer_fewer_suppliers', 'prefer_fewer_moves', 'not_modeled'].includes(f.rule_type)) {
    const filter = {};
    ['level_1', 'level_2', 'site', 'search'].forEach((k) => { if (f[k]) filter[k] = f[k]; });
    input.selection = { filter };
  }
  return input;
}

function formFromRule(r) {
  const p = r.params || {};
  const f = (r.selection && r.selection.filter) || {};
  const shares = Object.entries(p.shares || {});
  return { ...emptyForm(), rule_type: r.rule_type, supplier_id: p.supplier_id ? String(p.supplier_id) : (shares[0] ? shares[0][0] : ''),
    supplier_b: shares[1] ? shares[1][0] : '', share_a: shares[0] ? shares[0][1] : 1, share_b: shares[1] ? shares[1][1] : 1,
    n: p.n || 2, percent: r.rule_type === 'threshold' ? (p.percent ?? '') : (p.percent ?? 5), share_pct: p.share_d ? Math.round((100 * (p.share_n ?? 1)) / p.share_d) : 20,
    amount: p.amount || '', description: r.description || '', allow_no_change: Boolean(p.allow_no_change),
    per: p.per || 'part', years: p.years || 1, days: p.days || 42, max_amount: p.max_amount || '',
    min_amount: p.min_amount || '', no_new_parts: Boolean(p.no_new_parts), risk_percent: p.risk_percent ?? '',
    scope: r.selection ? 'filter' : 'all', level_1: f.level_1 || '', level_2: f.level_2 || '', site: f.site || '',
    search: f.search || '', editing: r,
    chosenLines: (r.selection && r.selection.line_uids) || null };
}

export default function RulesTab({ api, eventId, summary, options, refreshKey }) {
  const [rules, setRules] = useState(null);
  const [names, setNames] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const currency = (summary.terms && summary.terms.currency) || 'USD';

  const load = useCallback(() => Promise.all([api.rules(eventId), api.suppliers(eventId)])
    .then(([r, s]) => {
      setRules(r.items);
      setSuppliers(s.suppliers);
      setNames(Object.fromEntries(s.suppliers.map((x) => [String(x.supplier_id), x.supplier_name])));
    }).catch((e) => setError(e.message)), [api, eventId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const run = async (action, input) => {
    setBusy(true);
    setError('');
    try {
      await api.ruleAction(eventId, action, input);
      await load();
      return true;
    } catch (e) {
      if (e.code === 'stale_rule') await load();
      setError(e.message);
      return false;
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const editable = ['draft', 'open', 'closed'].includes(summary.status);
  const categories = (options && options.categories) || [];
  const sites = (options && options.sites) || [];
  const level2 = (categories.find((c) => c.level_1 === (form && form.level_1)) || { level_2: [] }).level_2;

  return (
    <section>
      <h2 className="srce-h2">Rules</h2>
      <div className="srce-mut srce-gap">
        Rules say what an award must respect. Scenarios choose which rules they use. A rule you make here is
        confirmed; a rule from the chat is proposed until you confirm it. Suppliers never see rules.
      </div>
      {error && <div className="srce-error" role="alert">{error}</div>}
      {editable && !form && (
        <button type="button" className="srce-btn" onClick={() => setForm(emptyForm())}>Add a rule</button>
      )}
      {form && (
        <div className="srce-card srce-gap">
          <label className="srce-col">Rule
            <select className="srce-input" value={form.rule_type} onChange={set('rule_type')}>
              {RULE_TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          {(NEEDS_SUPPLIER.includes(form.rule_type) || form.rule_type === 'split') && (
            <label className="srce-col">Supplier
              <select className="srce-input" value={form.supplier_id} onChange={set('supplier_id')}>
                <option value="">Choose…</option>
                {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>)}
              </select>
            </label>
          )}
          {form.rule_type === 'split' && (
            <span className="srce-row">
              <label className="srce-col">Share<input className="srce-input" type="number" min="0" value={form.share_a} onChange={set('share_a')} /></label>
              <label className="srce-col">Other supplier
                <select className="srce-input" value={form.supplier_b} onChange={set('supplier_b')}>
                  <option value="">Choose…</option>
                  {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>)}
                </select>
              </label>
              <label className="srce-col">Share<input className="srce-input" type="number" min="0" value={form.share_b} onChange={set('share_b')} /></label>
            </span>
          )}
          {['max_suppliers', 'min_sources'].includes(form.rule_type) && (
            <label className="srce-col">Number of suppliers<input className="srce-input" type="number" min="1" value={form.n} onChange={set('n')} /></label>
          )}
          {form.rule_type === 'min_sources' && (
            <label className="srce-col">Each supplier at least (%)<input className="srce-input" type="number" min="0" max="100" value={form.share_pct} onChange={set('share_pct')} /></label>
          )}
          {form.rule_type === 'threshold' && (
            <span className="srce-row">
              <label className="srce-col">Saving needed to move a line (%)<input className="srce-input" type="number" min="0" max="99" value={form.percent} onChange={set('percent')} /></label>
              <label className="srce-col">Or at least ({currency} a year)<input className="srce-input" inputMode="decimal" value={form.amount} onChange={set('amount')} /></label>
              <label className="srce-col">Counted
                <select className="srce-input" value={form.per} onChange={set('per')}>
                  <option value="part">Per part, all sites together</option>
                  <option value="site">Per site</option>
                </select></label>
            </span>
          )}
          {form.rule_type === 'supplier_limits' && (
            <span className="srce-row">
              <label className="srce-col">At most ({currency} a year)<input className="srce-input" inputMode="decimal" value={form.max_amount} onChange={set('max_amount')} /></label>
              <label className="srce-col">At least ({currency} a year)<input className="srce-input" inputMode="decimal" value={form.min_amount} onChange={set('min_amount')} /></label>
              <label className="srce-col">Risk cost (%)<input className="srce-input" type="number" min="1" max="100" value={form.risk_percent} onChange={set('risk_percent')} /></label>
              <label className="srce-inline"><input type="checkbox" checked={form.no_new_parts} onChange={set('no_new_parts')} /> No new parts</label>
            </span>
          )}
          {form.rule_type === 'lead_time_limit' && (
            <label className="srce-col">Longest lead time (days)<input className="srce-input" type="number" min="1" max="3650" value={form.days} onChange={set('days')} /></label>
          )}
          {form.rule_type === 'qualification_cost' && (
            <span className="srce-row">
              <label className="srce-col">What qualifying costs ({currency})<input className="srce-input" inputMode="decimal" value={form.amount} onChange={set('amount')} /></label>
              <label className="srce-col">Counted
                <select className="srce-input" value={form.per} onChange={set('per')}>
                  <option value="part">For each part</option>
                  <option value="supplier">For each new supplier</option>
                </select></label>
              <label className="srce-col">Spread over (years)<input className="srce-input" type="number" min="1" max="10" value={form.years} onChange={set('years')} /></label>
            </span>
          )}
          {form.rule_type === 'one_supplier' && (
            <label className="srce-inline"><input type="checkbox" checked={form.allow_no_change} onChange={set('allow_no_change')} /> Today's supplier may keep it as today</label>
          )}
          {['prefer_fewer_suppliers', 'prefer_fewer_moves'].includes(form.rule_type) && (
            <label className="srce-col">{form.rule_type === 'prefer_fewer_suppliers' ? 'Worth per extra supplier' : 'Worth per unit moved'} ({currency})
              <input className="srce-input" inputMode="decimal" value={form.amount} onChange={set('amount')} />
              <span className="srce-sub">A preference is not money you pay. The result shows what it costs.</span>
            </label>
          )}
          {form.rule_type === 'not_modeled' && (
            <label className="srce-col">Describe the need (for example a capacity limit)
              <textarea className="srce-input srce-textarea" maxLength={1000} value={form.description} onChange={set('description')} /></label>
          )}
          {form.chosenLines && <div className="srce-sub">This rule names {form.chosenLines.length} chosen lines; editing keeps them.</div>}
          {!form.chosenLines && !['prefer_fewer_suppliers', 'prefer_fewer_moves', 'not_modeled'].includes(form.rule_type) && (
            <div className="srce-col">
              {SCOPE_CHOICE.includes(form.rule_type) && (
                <span className="srce-row">
                  <label className="srce-inline"><input type="radio" checked={form.scope === 'all'} onChange={() => setForm({ ...form, scope: 'all' })} /> The whole event</label>
                  <label className="srce-inline"><input type="radio" checked={form.scope !== 'all'} onChange={() => setForm({ ...form, scope: 'filter' })} /> Only these lines</label>
                </span>
              )}
              {(!SCOPE_CHOICE.includes(form.rule_type) || form.scope !== 'all') && (
                <span className="srce-row">
                  <label className="srce-col">Category
                    <select className="srce-input" value={form.level_1} onChange={(e) => setForm({ ...form, level_1: e.target.value, level_2: '' })}>
                      <option value="">Any</option>
                      {categories.map((c) => <option key={c.level_1} value={c.level_1}>{c.label || c.level_1}</option>)}
                    </select></label>
                  {level2.length > 0 && (
                    <label className="srce-col">Subcategory
                      <select className="srce-input" value={form.level_2} onChange={set('level_2')}>
                        <option value="">Any</option>
                        {level2.map((c) => <option key={c.level_2} value={c.level_2}>{c.label || c.level_2}</option>)}
                      </select></label>
                  )}
                  <label className="srce-col">Site
                    <select className="srce-input" value={form.site} onChange={set('site')}>
                      <option value="">Any</option>
                      {sites.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></label>
                  <label className="srce-col">Part contains<input className="srce-input" maxLength={200} value={form.search} onChange={set('search')} /></label>
                </span>
              )}
              <span className="srce-sub">A rule on matching lines also covers lines added later that match.</span>
            </div>
          )}
          <span className="srce-row">
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy}
              onClick={async () => {
                const ok = form.editing
                  ? await run('edit', { ...formToInput(form), rule_id: form.editing.rule_id, expected_version: form.editing.current_version })
                  : await run('add', formToInput(form));
                if (ok) setForm(null);
              }}>{form.editing ? 'Save the change' : 'Save the rule'}</button>
            <button type="button" className="ll-clr" onClick={() => setForm(null)}>Cancel</button>
          </span>
        </div>
      )}
      {!rules && !error && <div className="ll-loading">Loading rules…</div>}
      {rules && rules.length === 0 && <div className="srce-mut">No rules yet.</div>}
      {rules && rules.length > 0 && (
        <ul className="srce-list">
          {rules.map((r) => (
            <li key={r.rule_id} className="srce-row">
              <span className="srce-col">
                <span>{ruleText(r, names, currency, categoryLabeler(options))}</span>
                <span className="srce-sub">
                  <span className={`srce-status srce-status--${r.state === 'confirmed' ? 'open' : r.state === 'rejected' ? 'cancelled' : 'draft'}`}>{STATE_TEXT[r.state]}</span>
                  {' '}{SOURCE_TEXT[r.source] || r.source}. Version {r.current_version}.
                </span>
              </span>
              {editable && (
                <span className="srce-push srce-row">
                  {r.state === 'proposed' && (
                    <button type="button" className="srce-btn" disabled={busy}
                      onClick={() => run('confirm', { rule_id: r.rule_id, expected_version: r.current_version })}>Confirm</button>
                  )}
                  <button type="button" className="srce-btn srce-btn--quiet" disabled={busy}
                    onClick={() => setForm(formFromRule(r))}>Edit</button>
                  {r.state !== 'rejected' && (
                    <button type="button" className="srce-btn srce-btn--quiet" disabled={busy}
                      onClick={() => run('reject', { rule_id: r.rule_id, expected_version: r.current_version })}>Reject</button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
